import type { MaterialMilestone } from "@edu/shared";

/**
 * Веха воспроизведения из позиции плеера: корзина 25, 50, 75 или 100.
 *
 * Проект `docs/material-receipt-design.md`, разделы 5, 6.2 и 10. Сервер
 * хранит БОЛЬШЕЕ из присланного, поэтому клиенту достаточно слать корзину,
 * когда она выросла; перемотка назад ничего не отматывает.
 *
 * ⚠️ **Веха означает «докуда дошло воспроизведение», а не «посмотрел»**
 * (раздел 6.2): перемотка в конец ставит 100 за три секунды. Слов «посмотрел»
 * и «досмотрел» из этого числа не следует нигде.
 *
 * 100 ставится с небольшим допуском до конца, а не на последнем кадре: плеер
 * часто встаёт на `ended`, не дойдя до `duration` на долю секунды, и без
 * допуска дошедший до конца получал бы «три четверти».
 */
const END_SLACK = 0.98;

export function milestoneAt(currentTime: number, duration: number): MaterialMilestone | null {
  // У потока без длины и у файла, ещё не прочитавшего метаданные, считать не от чего
  if (!Number.isFinite(duration) || duration <= 0) return null;
  if (!Number.isFinite(currentTime) || currentTime <= 0) return null;
  const share = currentTime / duration;
  if (share >= END_SLACK) return 100;
  if (share >= 0.75) return 75;
  if (share >= 0.5) return 50;
  if (share >= 0.25) return 25;
  return null;
}

/**
 * Слать ли веху сейчас.
 *
 * `confirmed` это наибольшая веха, которую сервер ПОДТВЕРДИЛ ответом, а не
 * последняя отправленная: сдвиг до ответа означал бы, что веха, потерянная в
 * обрыве, не досылается никогда (у 100 следующей корзины нет вовсе).
 * `inFlight` это веха, запрос по которой ещё летит, либо `0`: пока она в
 * пути, та же или меньшая не шлётся второй раз параллельно, а большая шлётся
 * сразу, сервер всё равно хранит большее.
 */
export function shouldSend(
  at: MaterialMilestone | null,
  confirmed: MaterialMilestone | 0,
  inFlight: MaterialMilestone | 0,
): at is MaterialMilestone {
  return at !== null && at > confirmed && at > inFlight;
}
